import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import ContactSheet from "@/components/ContactSheet";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
interface Project {
  company: string;
  focus: string;
  year: string;
  category: string;
  link?: string;
}

// Category display order
const categoryOrder = ["Framer Web Development", "E-Commerce Design & Development", "Squarespace Web Development", "Marketplace Product Creation", "Framer Templates", "Product Design", "Copywriting", "Email Design"];
const FilterDropdown = ({
  value,
  onChange,
  options,
  labelMap,
  className = ""






}: {value: string;onChange: (v: string) => void;options: string[];labelMap: (v: string) => string;className?: string;}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return <div ref={ref} className={`relative ${className}`}>
      <button onClick={() => setOpen(!open)} className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1.5 border transition-all duration-300 flex items-center gap-2 ${value !== "All" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground"}`}>
        {labelMap(value)}
        <svg className={`w-2 h-2 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 10 6" stroke="currentColor" strokeWidth="2">
          <path d="m1 1 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="absolute top-full left-0 mt-1 z-50 bg-background border border-border shadow-lg min-w-[200px] max-h-[260px] overflow-y-auto">
          {options.map((opt) => <button key={opt} onClick={() => {
        onChange(opt);
        setOpen(false);
      }} className={`w-full text-left px-3 py-2 font-body text-ts-2xs tracking-wide transition-colors ${value === opt ? "bg-foreground text-background font-medium" : "text-foreground hover:bg-muted"}`}>
              {labelMap(opt)}
            </button>)}
        </div>}
    </div>;
};
const Clients = () => {
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeYear, setActiveYear] = useState("All");
  useEffect(() => {
    const fetchProjects = async () => {
      const {
        data,
        error
      } = await supabase.from("client_projects").select("company, focus, year, category, link, display_order").order("display_order", {
        ascending: true
      });
      if (!error && data) {
        const projects = data.map((row) => ({
          company: row.company,
          focus: row.focus,
          year: row.year,
          category: row.category,
          link: row.link ?? undefined
        }));
        projects.sort((a, b) => b.year.localeCompare(a.year));
        setAllProjects(projects);
      }
      setLoading(false);
    };
    fetchProjects();
  }, []);
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allProjects.forEach((p) => cats.add(p.category));
    const ordered = categoryOrder.filter((c) => cats.has(c));
    for (const c of cats) {
      if (!categoryOrder.includes(c)) ordered.push(c);
    }
    return ["All", ...ordered];
  }, [allProjects]);
  const years = useMemo(() => {
    const yrs = new Set<string>();
    allProjects.forEach((p) => yrs.add(p.year));
    return ["All", ...Array.from(yrs).sort((a, b) => b.localeCompare(a))];
  }, [allProjects]);
  const filtered = useMemo(() => {
    return allProjects.filter((p) => {
      if (activeCategory !== "All" && p.category !== activeCategory) return false;
      if (activeYear !== "All" && p.year !== activeYear) return false;
      return true;
    });
  }, [allProjects, activeCategory, activeYear]);
  const projectsByCategory = useMemo(() => {
    const grouped = new Map<string, Project[]>();
    for (const p of filtered) {
      const list = grouped.get(p.category) || [];
      list.push(p);
      grouped.set(p.category, list);
    }
    const sorted = categoryOrder.filter((cat) => grouped.has(cat)).map((cat) => ({
      heading: cat,
      projects: grouped.get(cat)!
    }));
    for (const [cat, projects] of grouped) {
      if (!categoryOrder.includes(cat)) {
        sorted.push({
          heading: cat,
          projects
        });
      }
    }
    return sorted;
  }, [filtered]);
  const totalProjects = filtered.length;
  return <div className="min-h-screen h-screen bg-background text-foreground flex flex-col overflow-hidden">
      <SEO
      title="Clients"
      description="A selection of brands, startups, and creatives I've collaborated with."
      canonical="/clients" />

      {/* Top nav */}
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border shrink-0">
        <Link to="/" className="flex items-center gap-2 font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity">
          <ArrowLeft size={12} />
          Back
        </Link>
        <ContactSheet>
          <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-40 hover:opacity-100 transition-opacity">
            Work With Us
          </button>
        </ContactSheet>
      </header>

      {/* Two-column layout */}
      <div className="flex-1 overflow-y-auto px-5 md:px-8 py-6 md:py-8">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5
      }} className="mb-6">
            <h1 className="font-display text-[clamp(2rem,5vw,4rem)] italic leading-[0.95] tracking-tight">
              Clients
            </h1>
            <p className="font-body text-ts-xs text-muted-foreground mt-2">
              A selection of brands, startups, and creatives I've collaborated with.
            </p>
          </motion.div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b border-border">
            {/* Category filters — dropdown on mobile, chips on desktop */}
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="font-body text-ts-2xs tracking-[0.2em] uppercase text-muted-foreground/40 mr-1">
                Type
              </span>
              {/* Mobile dropdown */}
              <FilterDropdown value={activeCategory} onChange={setActiveCategory} options={categories} labelMap={(v) => v === "All" ? "All Types" : v} className="md:hidden" />
              {/* Desktop chips */}
              <div className="hidden md:flex flex-wrap gap-1.5">
                {categories.map((cat) => <button key={cat} onClick={() => setActiveCategory(cat)} className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1 border transition-all duration-300 ${activeCategory === cat ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"}`}>
                    {cat === "All" ? "All Types" : cat}
                  </button>)}
              </div>
            </div>

            {/* Year filters — dropdown on mobile, chips on desktop */}
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="font-body text-ts-2xs tracking-[0.2em] uppercase text-muted-foreground/40 mr-1">
                Year
              </span>
              <FilterDropdown value={activeYear} onChange={setActiveYear} options={years} labelMap={(v) => v === "All" ? "All Years" : v} className="md:hidden" />
              <div className="hidden md:flex flex-wrap gap-1.5">
                {years.map((yr) => <button key={yr} onClick={() => setActiveYear(yr)} className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1 border transition-all duration-300 ${activeYear === yr ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"}`}>
                    {yr === "All" ? "All Years" : yr}
                  </button>)}
              </div>
            </div>
          </div>

          {loading ? <p className="font-body text-ts-2xs text-muted-foreground">Loading…</p> : filtered.length === 0 ? <p className="font-body text-ts-xs text-muted-foreground/60 py-8 text-center">
              No projects match the selected filters.
            </p> : <div className="columns-1 md:columns-2 gap-x-10">
              {projectsByCategory.map((section, sIdx) => <motion.section key={section.heading} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: sIdx * 0.06,
          duration: 0.4
        }} className="mb-6 break-inside-avoid">
                  <h2 className="font-body text-ts-2xs font-bold tracking-[0.3em] uppercase opacity-35 mb-2">
                    {section.heading}
                  </h2>
                  {section.projects.map((project, pIdx) => {
            const content = <>
                        <span className="font-body text-ts-xs opacity-50 tabular-nums shrink-0 w-10">
                          {project.year}
                        </span>
                        <span className="font-heading text-ts-xs md:text-ts-sm tracking-tight shrink-0" style={{ fontWeight: 500 }}>
                          {project.company}
                        </span>
                        <span className="font-body text-ts-xs opacity-50 truncate">
                          {project.focus}
                        </span>
                      </>;
            return project.link ? <a key={`${project.company}-${pIdx}`} href={project.link} target={project.link.startsWith("/") ? undefined : "_blank"} rel={project.link.startsWith("/") ? undefined : "noopener noreferrer"} className="flex items-baseline gap-3 py-[5px] group cursor-pointer hover:opacity-80 transition-opacity">
                        {content}
                        <span className="font-body text-ts-2xs opacity-0 group-hover:opacity-40 transition-opacity tracking-[0.15em] uppercase ml-auto shrink-0">
                          View ↗
                        </span>
                      </a> : <div key={`${project.company}-${pIdx}`} className="flex items-baseline gap-3 py-[5px] group">
                        {content}
                      </div>;
          })}
                </motion.section>)}
            </div>}
      </div>
    </div>;
};
export default Clients;